import React, {useEffect, useRef, useState} from 'react';
import {
  Button,
  Card,
  Divider,
  List, message,
  Space,
  Steps,
  Typography,
} from "antd";
import {DeleteOutlined} from "@ant-design/icons";
import {Formik} from "formik";
import {Form, SubmitButton} from "formik-antd";

import SireInfoForm from "./SireInfoForm";
import OwnerInfoForm from "./OwnerInfoForm";
import MarketingInfoForm from "./MarketingInfoForm";
import FormReview from "./FormReview";
import SirePaymentForm from "../SirePaymentForm";
import axios from "axios";
import {useRealmApp} from "../../utils/RealmApp";
import {getSireEnrollment} from "../../services/sireEnrollments";
import {
  EMPTY_ENROLLMENT_VALUES,
  createBlankDraft,
  getDraft,
  listDrafts,
  removeDraft,
  saveDraft,
} from "./sireEnrollmentDrafts";

const SireEnrollmentIndex = () => {
  const {Title, Paragraph, Text} = Typography;
  const [current, setCurrent] = useState(0);
  const [isStarted, setIsStarted] = useState(false);
  const [api, contextHolder] = message.useMessage();
  const [savedValues, setSavedValues] = useState(null);
  const [enrolledSireId, setEnrolledSireId] = useState(null);
  const [activeDraftId, setActiveDraftIdState] = useState(null);
  const [draftSummaries, setDraftSummaries] = useState([]);
  const ref = useRef(null);

  const app = useRealmApp()

  const openNotificationWithIcon = (type, title, description, placement) => {
    api[type]({
      message: title,
      description: description,
      placement,
    });
  };

  const refreshDraftList = () => {
    setDraftSummaries(listDrafts());
  };

  useEffect(() => {
    refreshDraftList();
  }, []);

  const scrollToForm = () => {
    setTimeout(() => {
      ref?.current?.scrollIntoView({behavior: "smooth"});
    }, 100);
  };

  const openDraft = (draftId) => {
    const draft = getDraft(draftId);
    if (!draft) {
      refreshDraftList();
      return;
    }
    setActiveDraftIdState(draftId);
    setSavedValues({
      ...draft.values,
      ...(draft.enrollmentId ? { _id: draft.enrollmentId } : {}),
    });
    setCurrent(draft.currentStep ?? 0);
    setEnrolledSireId(draft.enrollmentId || null);
    setIsStarted(true);
    scrollToForm();
  };

  const handleStartNew = () => {
    const draftId = createBlankDraft();
    setActiveDraftIdState(draftId);
    setSavedValues({ ...EMPTY_ENROLLMENT_VALUES });
    setCurrent(0);
    setEnrolledSireId(null);
    setIsStarted(true);
    refreshDraftList();
    scrollToForm();
  };

  const handleDeleteDraft = (draftId, event) => {
    event?.stopPropagation?.();
    removeDraft(draftId);
    if (activeDraftId === draftId) {
      setActiveDraftIdState(null);
      setSavedValues(null);
      setEnrolledSireId(null);
      setCurrent(0);
      setIsStarted(false);
    }
    refreshDraftList();
  };

  const persistActiveDraft = (values, { currentStep, enrollmentId } = {}) => {
    if (!activeDraftId) return;
    saveDraft(activeDraftId, {
      values,
      currentStep: currentStep !== undefined ? currentStep : current,
      enrollmentId: enrollmentId !== undefined ? enrollmentId : enrolledSireId,
    });
    refreshDraftList();
  };

  const pricingData = ['$2500 = $2600'];

  const handleNextAndSave = (values, errors = {}) => {
    if (!Object.entries(errors).length) {
      const nextStep = current + 1;
      persistActiveDraft(values, { currentStep: nextStep });
      setCurrent(nextStep);
    } else {
      persistActiveDraft(values);
    }
  };

  const handleCreateEnrollment = async (values) => {
    try {
      const normalizeName = (value) => {
        if (typeof value !== 'string') return value;
        return value.trim().toUpperCase().replace(/\s+/g, ' ');
      };
      const toUpperCaseIfString = (value) => (typeof value === 'string' ? value.toUpperCase() : value);
      // Keep these fields exactly as entered/generated (no upper-casing), so the
      // stored signature name, agreed statement, and rules version are verbatim.
      const preserveCaseKeys = new Set(['country', 'signedName', 'signedStatement', 'signedRulesVersion', 'signedAt']);
      const transformedValues = {};
      for (const key in values) {
        if (values.hasOwnProperty(key)) {
          if (key === 'name') {
            transformedValues[key] = normalizeName(values[key]);
          } else {
            transformedValues[key] = preserveCaseKeys.has(key) ? values[key] : toUpperCaseIfString(values[key]);
          }
        }
      }

      if (enrolledSireId) {
        transformedValues.enrollmentId = enrolledSireId;
      }

      const result = await axios.post(`${process.env.REACT_APP_MANAGEMENT_API_URL}/sire-enrollment/enroll`, transformedValues)
      if (result.data.success) {
        const newSireId = result.data.enrollment?.insertedId
          || result.data.enrollment?.value?._id
          || enrolledSireId;
        const enrollmentIdStr = newSireId ? String(newSireId) : null;
        const newEnrollment = await getSireEnrollment(app, enrollmentIdStr);
        setEnrolledSireId(enrollmentIdStr)
        setSavedValues(newEnrollment)
        persistActiveDraft(values, {
          currentStep: current + 1,
          enrollmentId: enrollmentIdStr,
        });
        setCurrent(current + 1);
      }
    } catch (e) {
      openNotificationWithIcon(
        "error",
        "Submit Form Failed",
        e?.response?.data?.error || "Please try again later or reach out to the support team.",
        "bottomRight",
      );
    }
  }

  const handleBack = (values) => {
    const nextStep = current - 1;
    if (values) {
      persistActiveDraft(values, { currentStep: nextStep });
    }
    setCurrent(nextStep);
  }
  const onStepChange = (value) => {
    setCurrent(value);
    if (activeDraftId) {
      const draft = getDraft(activeDraftId);
      if (draft) {
        persistActiveDraft(draft.values, { currentStep: value });
      }
    }
  };


  const items = [
    {
      title: '1',
      description: 'Stallion Information',
    },
    {
      title: '2',
      description: 'Owner Information',
    },
    {
      title: '3',
      description: 'Marketing Information',
    },
    {
      title: '4',
      description: 'Review',
    },
    {
      title: '5',
      description: 'Payment',
    },
  ]

  const initialValues = {
    ...EMPTY_ENROLLMENT_VALUES,
    // Step 1
    name: savedValues?.name || '',
    birthYear: savedValues?.birthYear || '',
    regNum: savedValues?.regNum || '',
    sireSire: savedValues?.sireSire || '',
    dam: savedValues?.dam || '',
    damSire: savedValues?.damSire || '',
    fee: savedValues?.fee || '',
    semenAvailability: savedValues?.semenAvailability || [],
    country: savedValues?.country || '',
    contact: savedValues?.contact || '',
    junior: savedValues?.junior || false,
    limited: savedValues?.limited || false,
    barrels: savedValues?.barrels || false,
    poles: savedValues?.poles || false,
    firstCropBirthYear: savedValues?.firstCropBirthYear || '',
    breedingReport: savedValues?.breedingReport || {},
    breedingList: savedValues?.breedingList || {},
    w9: savedValues?.w9 || {},

    // Step 2
    ownerName: savedValues?.ownerName || '',
    ownerEmail: savedValues?.ownerEmail || '',
    ownerAddress1: savedValues?.ownerAddress1 || '',
    ownerAddress3: savedValues?.ownerAddress3 || '',
    ownerPhone: savedValues?.ownerPhone || '',
    signed: savedValues?.signed || false,
    signedAt: savedValues?.signedAt || null,
    signedName: savedValues?.signedName || '',
    signedStatement: savedValues?.signedStatement || '',
    signedRulesVersion: savedValues?.signedRulesVersion || '',

    // Step 3
    stationName: savedValues?.stationName || '',
    stationAddress1: savedValues?.stationAddress1 || '',
    stationAddress3: savedValues?.stationAddress3 || '',
    stationEmail: savedValues?.stationEmail || '',
    stationPhone: savedValues?.stationPhone || '',
    stationWebsite: savedValues?.stationWebsite || '',
    socialMedia: savedValues?.socialMedia || '',
    website: savedValues?.website || '',
    marketingParagraph: savedValues?.marketingParagraph || '',
    papers: savedValues?.papers || {},
    headPicture: savedValues?.headPicture || {},
    profilePicture: savedValues?.profilePicture || {},
    video: savedValues?.video || {},
  };

  const validate = async (values) => {
    const errors = {};
    if (current === 0) {
      if (!values.name) errors.name = 'Name is required';
      if (!values.birthYear) errors.birthYear = 'Birth Year is required';
      if (!values.regNum) {
        errors.regNum = 'Registration Number is required'
      }
      if (!values.sireSire) errors.sireSire = 'Sire is required';
      if (!values.dam) errors.dam = 'Dam is required';
      if (!values.damSire) errors.damSire = 'Dam Sire is required';
      if (!values.country) errors.country = 'Country is required';
      if (!values.semenAvailability) errors.semenAvailability = 'Semen Availability is required';
      if (!values.contact) errors.contact = 'Contact Person is required';
      if (!values.fee) errors.fee = 'Fee is required';
      if (values.junior && !values.firstCropBirthYear) errors.firstCropBirthYear = 'Year is required for all junior Stallions'

      if (!values.breedingReport || Object.entries(values.breedingReport).length === 0) {
        errors.breedingReport = 'Breeding Report is required'
      }

      if (!values.w9 || (values.w9 && Object.entries(values.w9).length === 0)) {
        errors.w9 = 'W9 is required'
      }
    }
    if (current === 1) {
      if (!values.ownerName) errors.ownerName = 'Name is required';
      if (!values.ownerEmail) {
        errors.ownerEmail = 'Email is required';
      } else if (!/^\S+@\S+\.\S+$/.test(values.ownerEmail)) {
        errors.ownerEmail = 'Invalid email address';
      }
      if (!values.ownerAddress1) errors.ownerAddress1 = 'Street Address is required';
      if (!values.ownerPhone) errors.ownerPhone = 'Owner Phone is required';
    }
    if (current === 2) {
      if (!values.stationName) errors.stationName = 'Stallion Station Name is required';
      if (values.stationEmail && !/^\S+@\S+\.\S+$/.test(values.stationEmail)) {
        errors.stationEmail = 'Invalid email address';
      }
      if (!values.marketingParagraph) errors.marketingParagraph = 'Marketing Paragraph is required';
      if (!values.papers || (values.papers && Object.entries(values.papers).length === 0)) errors.papers = 'Required'

    }
    // The signature is now captured on the payment step (last step before pay),
    // so the Review step no longer gates on it.
    return errors;
  }

  const formatDraftUpdatedAt = (updatedAt) => {
    if (!updatedAt) return '';
    try {
      return new Date(updatedAt).toLocaleString();
    } catch {
      return '';
    }
  };

  return (
    <>
      {!isStarted &&
        <Card>
          <Paragraph>
            At this time
            we only offer online payment via credit card, and charge a{" "}
            <strong>4% additional fee </strong> to cover the credit card
            processing fee.
          </Paragraph>
          <Divider orientation="center">Pricing with Fees</Divider>
          <List
            size="small"
            bordered
            dataSource={pricingData}
            renderItem={(item) => <List.Item>{item}</List.Item>}
          />
          <div style={{marginTop: "20px", textAlign: "center"}}>
            <Button
              type="primary"
              size="large"
              onClick={handleStartNew}>
              Start New Application
            </Button>
          </div>
          {draftSummaries.length > 0 && (
            <div style={{marginTop: "24px"}}>
              <Divider orientation="center">In Progress</Divider>
              <List
                bordered
                dataSource={draftSummaries}
                renderItem={(draft) => (
                  <List.Item
                    actions={[
                      <Button
                        key="continue"
                        type="link"
                        onClick={() => openDraft(draft.id)}
                      >
                        Continue
                      </Button>,
                      <Button
                        key="delete"
                        type="text"
                        danger
                        aria-label={`Delete draft ${draft.name}`}
                        icon={<DeleteOutlined />}
                        onClick={(e) => handleDeleteDraft(draft.id, e)}
                      />,
                    ]}
                  >
                    <List.Item.Meta
                      title={draft.name}
                      description={
                        <Space direction="vertical" size={0}>
                          <Text type="secondary">
                            Step {(draft.currentStep ?? 0) + 1} of 5
                            {draft.enrollmentId ? ' · Submitted — awaiting payment' : ''}
                          </Text>
                          {draft.updatedAt && (
                            <Text type="secondary">
                              Updated {formatDraftUpdatedAt(draft.updatedAt)}
                            </Text>
                          )}
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            </div>
          )}
        </Card>}


      {isStarted &&
        <>
          {contextHolder}
          <Title level={4} ref={ref}>
            Please use this form to enroll Stallions.
          </Title>

          <Card style={{minHeight: "50vh"}}>


            <Formik key={activeDraftId || 'new'}
                    initialValues={initialValues}
                    validate={validate}
                    onSubmit={handleCreateEnrollment}>
              {({values, handleChange, errors, setFieldValue, setTouched, validateForm}) => (

                <div>
                  <div style={{margin: "20px 0 50px 0"}}>
                    <Steps
                      size="medium"
                      current={current}
                      items={items}
                      status={Object.entries(errors).length ? 'error' : ''}
                    />
                  </div>

                  <Form
                    layout="vertical"
                  >

                    {current === 0 &&
                      <SireInfoForm
                        handleChange={handleChange}
                        values={values}
                        setFieldValue={setFieldValue}
                        identityLocked={!!enrolledSireId}
                      />}

                    {current === 1 &&
                      <OwnerInfoForm
                        setFieldValue={setFieldValue}
                        errors={errors}
                        values={values}
                      />}

                    {current === 2 &&
                      <MarketingInfoForm values={values} setFieldValue={setFieldValue}/>}
                    <Divider/>

                    {current === 3 &&
                      <>
                        <FormReview values={values} onStepChange={onStepChange}/>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: 'flex-end',
                            gap: "10px",
                          }}>
                          <Form.Item label="" name="submit">
                            <SubmitButton
                              size="large"
                            >
                              Enroll Stallion and Pay
                            </SubmitButton>
                          </Form.Item>
                        </div>
                      </>}

                    {(current === 0 ||
                        current === 1 ||
                        current === 2) &&
                      <div
                        style={{
                          display: "flex",
                          justifyContent: 'flex-end',
                          gap: "10px",
                        }}>
                        {current !== 0 &&
                          <Button
                            size="large"
                            onClick={() => handleBack(values)}>
                            Back
                          </Button>}
                        {(current !== 4 || current !== 5) &&
                          <Button
                            size="large"
                            onClick={async () => {
                              const errors = await validateForm(values);
                              setTouched(Object.keys(errors).reduce((acc, key) => ({ ...acc, [key]: true }), {}));

                              if (Object.keys(errors).length === 0) {
                                handleNextAndSave(values, errors);
                              }
                            }}>
                            Save and Next
                          </Button>}
                      </div>
                    }
                  </Form>
                </div>
              )}

            </Formik>
            {current === 4 &&
              <>
                <SirePaymentForm
                  onStepChange={() => setCurrent(3)}
                  data={savedValues}
                  sireName={savedValues?.name}
                  birthYear={savedValues?.birthYear}
                  regNum={savedValues?.regNum}
                  ownerName={savedValues?.ownerName}
                  ownerEmail={savedValues?.ownerEmail}
                  sireId={enrolledSireId}
                  draftId={activeDraftId}
                  applicationType='Enrollment'
                />
              </>
            }

          </Card>

        </>
      }

    </>
  );
};


export default SireEnrollmentIndex;
